/**
 * This module is a selection of function components used for rendering the general purpose state-less react components.
 * @module
 */

import * as React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import type {Variable} from "./GeneralInterfaces.tsx";
import {VariableTypeEnum} from "../Settings.tsx";
import {Badge} from "react-bootstrap";

/**
 * This is the set of properties for the centering margins component.
 */
type CenteringMarginsProps = {
    /** Specifies the width of the element. Required such that corresponding centering margins can be calculated. */
    width: number | string;
    /** The element's children to be included in the element's body */
    children: React.ReactNode;
}


/**
 * This function component centers the contained elements by automatically calculating and applying adequate margins.
 * @param props Properties containing children and desired width.
 * @returns React element
 */
export const CenteringMargins: React.FC<CenteringMarginsProps> = (props: CenteringMarginsProps) => {
    return (
        <div className="mx-auto" style={{width: props.width}}>{props.children}</div>
    );
}


/**
 * This is the set of properties for the middle column component.
 */
type MiddleColumnProps = {
    /** Specifies the number of columns to be grouped together for the middle column component. */
    cols: number;
    /** Extra CSS styling for the Row element */
    extra_style?: React.CSSProperties;
    /** The element's children to be included in the element's body */
    children: React.ReactNode;
}


/**
 * This function component starts a new bootstrap row and places the children in a centered column. To center this
 * column additional columns are inserted before and after this middle column.
 * @param props Properties containing children and desired width (as number of bootstrap grid columns).
 * @returns React element
 */
export const MiddleColumn: React.FC<MiddleColumnProps> = (props: MiddleColumnProps) => {
    const wrapping_columns = Math.round((12 - props.cols) / 2 - 0.1);
    const middle_columns = 12 - 2 * wrapping_columns;
    return (
        <Row style={props.extra_style}>
            {wrapping_columns > 0 ? <Col sm={wrapping_columns}/> : undefined}
            <Col sm={middle_columns}>
                {props.children}
            </Col>
            {wrapping_columns > 0 ? <Col sm={wrapping_columns}/> : undefined}
        </Row>
    );
}


/**
 * This function component returns a div element with 20 points of top padding. It is meant to provide vertical spacing
 * for cosmetic reasons where desired.
 * @returns React element
 */
export const VerticalSpacing: React.FC = () => {
    return <div style={{paddingTop: 20}}/>
}


/**
 * Props for the VariablesBadges component.
 */
type VariablesBadgesProps = {
    /** List of variables to display as badges */
    variables: Variable[];
    /** Whether badges should be draggable */
    draggable?: boolean;
    /** Callback when a draggable badge is dragged */
    onDragStart?: (variable: Variable, e: React.DragEvent) => void;
    lightWeight?: boolean;
};


/**
 * Renders a list of variable badges.
 * Supports optional dragging and custom drag start handler.
 * Variables are sorted first by type, then by name.
 */
export const VariablesBadges: React.FC<VariablesBadgesProps> = (props: VariablesBadgesProps) => {
    /**
     * Maps each VariableTypeEnum to a corresponding Bootstrap badge variant.
     */
    const enumTypeMap: Record<VariableTypeEnum, string> = {
        [VariableTypeEnum.PORTFOLIO]: "primary",
        [VariableTypeEnum.TIME_SERIES]: "info",
        [VariableTypeEnum.FORMULA]: "success",
        [VariableTypeEnum.TRIGGER]: "warning",
        [VariableTypeEnum.WATERFALL]: "danger",
    };

    /**
     * Default Bootstrap variant for variable badges.
     */
    const defaultVariant = "secondary";

    /**
     * Maps variable types to badge variants using enumTypeMap.
     * If no mapping exists, defaultVariant is used.
     */
    const typeToVariant: Record<string, string> = {};
    for (const value of Object.values(VariableTypeEnum)) {
        typeToVariant[value] = enumTypeMap[value as VariableTypeEnum] || defaultVariant;
    }

    /**
     * Sorts variables by type, then alphabetically by name.
     */
    const sortedVariables = [...props.variables].sort((a, b) => {
        const typeA = a.type.toString();
        const typeB = b.type.toString();
        if (typeA < typeB) return -1;
        if (typeA > typeB) return 1;
        return a.name.localeCompare(b.name);
    });

    return (
        <div>
            {sortedVariables.map(variable => (
                <div key={variable.id} style={{ marginTop: "0.25rem", marginBottom: "0.25rem" }}>
                    <Badge
                        pill
                        bg={typeToVariant[variable.type] || defaultVariant}
                        title={variable.description}
                        style={{
                            fontSize: "1rem",
                            fontWeight: props.lightWeight ? 320 : 600, // Less bold font
                            cursor: props.draggable ? "grab" : undefined,
                            userSelect: "none"
                        }}
                        draggable={props.draggable}
                        onDragStart={props.draggable && props.onDragStart ? e => props.onDragStart!(variable, e) : undefined}
                    >
                        {variable.name}
                    </Badge>
                </div>
            ))}
        </div>
    );
};